import { Fragment, useId, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { cx, errorClass, labelClass } from './styles';

interface OtpInputProps {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onComplete?: (value: string) => void;
  readonly length?: number;
  readonly error?: string | undefined;
  readonly isDisabled?: boolean;
  readonly autoFocus?: boolean;
}

function keepCaretAtEnd(event: SyntheticEvent<HTMLInputElement>) {
  const input = event.currentTarget;
  const end = input.value.length;
  if (input.selectionStart !== end || input.selectionEnd !== end) {
    input.setSelectionRange(end, end);
  }
}

export function OtpInput({
  label,
  value,
  onChange,
  onComplete,
  length = 6,
  error,
  isDisabled = false,
  autoFocus = false,
}: OtpInputProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const [focused, setFocused] = useState(false);
  const active = Math.min(value.length, length - 1);
  const middle = Math.ceil(length / 2);

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <div className={cx('relative', error ? 'motion-safe:animate-shake' : null)}>
        <input
          id={id}
          value={value}
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="[0-9]*"
          maxLength={length}
          spellCheck={false}
          autoFocus={autoFocus}
          disabled={isDisabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          onFocus={() => {
            setFocused(true);
          }}
          onBlur={() => {
            setFocused(false);
          }}
          onSelect={keepCaretAtEnd}
          onChange={(event) => {
            const next = event.target.value.replace(/\D/g, '').slice(0, length);
            onChange(next);
            if (next.length === length && next !== value) {
              onComplete?.(next);
            }
          }}
          className="absolute inset-0 z-10 size-full cursor-text bg-transparent text-base text-transparent caret-transparent outline-none selection:bg-transparent disabled:cursor-not-allowed"
        />
        <div aria-hidden="true" className="flex items-center gap-2 sm:gap-2.5">
          {Array.from({ length }, (_, index) => {
            const digit = value[index];
            const current = focused && index === active && value.length < length;
            return (
              <Fragment key={index}>
                {index === middle ? (
                  <span className="h-0.5 w-2.5 shrink-0 rounded-full bg-slate-300 sm:w-3" />
                ) : null}
                <span
                  className={cx(
                    'flex h-14 min-w-0 flex-1 items-center justify-center rounded-2xl border bg-white font-display text-2xl font-bold text-slate-900 shadow-[inset_0_1px_2px_rgb(15_23_42/0.05)] transition duration-150',
                    error
                      ? 'border-red-300 bg-red-50/40'
                      : current
                        ? 'border-brand-500 ring-4 ring-brand-500/15'
                        : digit
                          ? 'border-slate-300'
                          : 'border-slate-200',
                    isDisabled ? 'opacity-60' : null,
                  )}
                >
                  {digit ? (
                    <span className="motion-safe:animate-pop">{digit}</span>
                  ) : current ? (
                    <span className="h-7 w-0.5 rounded-full bg-brand-600 motion-safe:animate-caret" />
                  ) : null}
                </span>
              </Fragment>
            );
          })}
        </div>
      </div>
      {error ? (
        <p id={errorId} className={errorClass}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
